import { useState } from 'react';

import TextareaInput from '../../../components/forms/raw-inputs/textarea-input.jsx';

import { Button } from '../../../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';

import ErrorSpan from '../../../components/layout/error-span.tsx';
import { checkAnyErrorForPath, retrieveErrorForPath, cn } from '../../../shared/utils';

interface NotesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    note: string;
    onChange: (value: string) => void;
    errors: Record<string, string>;
    disabled: boolean;
}

/* Migrat de pe un overlay scris de mana pe `ui/dialog` (Radix).
   Ce se castiga si nu se putea obtine altfel: capcana de focus. Varianta de
   dinainte se putea tabula din dialog direct in tabelul de dedesubt, iar un
   cititor de ecran continua sa citeasca pagina — dialogul nu se anunta ca modal
   decat prin `role`/`aria-modal` scrise de mana. Radix pune si `aria-hidden` pe
   restul paginii.

   ⚠️ NU si intoarcerea focusului pe declansator: sesiunea de imagine a masurat ca
   dupa `Escape` focusul ajunge pe viewport-ul de scroll (la 1280) sau pe «Toggle
   Sidebar» (la 360), desi butonul declansator e inca in DOM. O versiune anterioara
   a comentariului asta o afirma fara s-o fi masurat nimeni; ramane o restanta
   reala, nu o proprietate castigata.

   Fratele lui, `school-requests/components/PhoneNotesDialog.tsx`, face acelasi
   lucru (notite telefonice) si era deja pe `ui/dialog`; forma de aici o urmeaza
   pe a lui, ca sa nu ramana doua dialoguri de notite cu doua infatisari.

   Ce se schimba VIZUAL fata de varianta de dinainte, deliberat:
   - fundalul din spate e `bg-dark/80` (standardul `DialogOverlay`), nu
     `bg-black/50 backdrop-blur-xs`;
   - panoul e `bg-white p-6` (standardul `DialogContent`), nu `bg-light p-4`;
   - apare un titlu vizibil, „Notițe telefonice"; inainte dialogul n-avea titlu,
     doar un `aria-label`, pentru ca nu exista text de referit;
   - butonul de inchidere e cel din `DialogContent` (are deja `touch-target`),
     nu unul propriu.

   Ce se schimba ca COMPORTAMENT: dialogul se inchide acum si cu `Escape`. Inainte
   se inchidea doar din «Anulare» sau din X.

   Clicul in afara panoului inchide dialogul DOAR daca textul n-a fost modificat.
   Radix il inchide implicit, iar sesiunea de imagine a masurat ca asa se pierde
   textul tastat, fara niciun avertisment — regresie de flux fata de overlay-ul de
   dinainte, care nu se inchidea deloc la clic in afara. `onInteractOutside`
   opreste inchiderea cat timp casuta difera de valoarea cu care s-a deschis
   dialogul. `Escape` ramane neconditionat: e gestul deliberat de abandon.

   Ce se pastreaza din reparatia masurata in `c28e1cfc`: butoanele se aseaza pe
   coloana sub `sm` (`DialogFooter` e `flex-col-reverse`), fiecare `w-full`, si
   panoul nu atinge marginile la 360px (`w-[calc(100vw-2rem)]`). Fara ele,
   «Salvare» iesea din ecran si la 360, si la 1280. */
export function NotesDialog({
    isOpen,
    onClose,
    onSave,
    note,
    onChange,
    errors,
    disabled
}: NotesDialogProps) {
    const hasError = checkAnyErrorForPath('phoneCallNote', errors);

    /* Valoarea cu care s-a deschis dialogul, ca sa stim daca s-a modificat ceva.
       Se fixeaza la TRANZITIA in deschis, nu la fiecare tastare: `note` vine de la
       parinte si se schimba in timp ce se scrie.

       🔴 Prima varianta tinea valoarea intr-un `useRef` fixat dintr-un `useEffect`.
       Nu mergea, si a picat exact pe cazul obisnuit — un rand care ARE deja notita:
       `useRef(note || '')` se initializeaza la MONTARE, cand dialogul e inchis si
       `note` e `''`; randarea in care `isOpen` devine `true` calculeaza `modificat`
       cu referinta VECHE, deci `true`; iar efectul care corecteaza referinta
       **nu declanseaza o re-randare**, deci `modificat` ramanea `true` in
       inchiderea capturata de `onInteractOutside`. Rezultat: pe un rand cu notita,
       clicul in afara nu mai inchidea niciodata dialogul, desi omul nu atinsese
       nimic. Gasit de sesiunea de imagine prin A/B pe `phoneCallNote`, singura
       variabila schimbata.

       Forma de acum foloseste STARE, nu referinta: ajustarea la schimbarea
       propului declanseaza o re-randare, deci `modificat` e corect chiar in
       randarea in care dialogul se deschide. */
    const [eraDeschis, setEraDeschis] = useState(isOpen);
    const [notaInitiala, setNotaInitiala] = useState(note || '');
    if (isOpen !== eraDeschis) {
        setEraDeschis(isOpen);
        if (isOpen) setNotaInitiala(note || '');
    }
    const modificat = (note || '') !== notaInitiala;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent
                className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:w-full sm:max-w-[500px]"
                onInteractOutside={(e) => { if (modificat) e.preventDefault(); }}
            >
                <DialogHeader>
                    <DialogTitle>Notițe telefonice</DialogTitle>
                </DialogHeader>

                <Field
                    className="w-full rounded-md"
                    data-invalid={hasError}
                    data-disabled={disabled}
                >
                    <div
                        className={cn(
                            /* Rama exterioara e TRANSPARENTA in starea normala, si asta e
                               deliberat, nu o declaratie moarta: latimea de 3px ramane ca
                               sa rezerve spatiul pentru `border-error` de mai jos, deci
                               trecerea in eroare nu misca nimic in pagina.
                               De ce nu mai e colorata: `textarea` are chenar propriu 1px
                               `--color-ring` la 7,40 SI fundal distinct (#dae7d6 pe alb),
                               deci interiorul e deja delimitat cu marja. Cu rama la 4,99,
                               ieseau doua inele concentrice la 8px distanta — masurat pe
                               ecran, se citeste ca greseala de layout, nu ca ierarhie. */
                            'flex w-full flex-col rounded-lg border-[3px] border-transparent bg-white px-2 shadow-2xl',
                            'transition-colors duration-300',
                            hasError && 'border-error',
                            'pb-2'
                        )}
                    >
                        <TextareaInput
                            name="phoneCallNote"
                            value={note || ''}
                            handleChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                onChange(e.target.value)
                            }
                            label="Notițe telefonice"
                            customStyles={note ? 'font-black' : 'sr-only'}
                            rows={4}
                            {...({} as any)}
                        />
                        <ErrorSpan
                            text={retrieveErrorForPath('phoneCallNote', errors)}
                            customStyles={cn(hasError ? 'flex' : 'hidden')}
                        />
                    </div>
                </Field>

                <DialogFooter className="gap-3 sm:gap-0">
                    <Button className="w-full sm:w-auto" disabled={disabled} onClick={onClose}>
                        Anulare
                    </Button>
                    <Button className="w-full sm:w-auto" disabled={disabled} onClick={onSave}>
                        Salvare
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
